import React, { FC } from 'react';
import { getIn, useFormik } from 'formik';

import { Column, FormInput, Row } from 'Shared/components';

import { useAppContext } from 'QuoteApp/services/providers';
import { generateFieldDataTestId } from 'QuoteApp/utils';
import { SummarySection } from 'QuoteApp/screens/Summary/SummarySection';
import {
  AgencyBundleType,
  transformSaveAgencyBundleRequest,
} from 'QuoteApp/services/endpoints/PostSaveAgencyBundle';
import { mergeInProgressDataFromSummary } from 'QuoteApp/screens/Summary/Summary';

export const AgencyBundle: FC = () => {
  const {
    appId,
    config,
    configLoading,
    details,
    setDetails,
    refreshRate,
    api,
  } = useAppContext();

  const {
    values,
    dirty,
    setFieldValue,
    getFieldProps,
    resetForm,
    submitForm,
    isSubmitting,
    setSubmitting,
  } = useFormik({
    initialValues: {
      [appId]: {
        carrier:
          details.agencyInformation?.attributes.agencyBundle.attributes.carrier
            .value || '',
        policyType:
          details.agencyInformation?.attributes.agencyBundle.attributes
            .policyType.value || 'none',
      },
    },
    onSubmit: async (data) => {
      try {
        const response = await api.saveAppData({
          data: data[appId],
          transformRequest: transformSaveAgencyBundleRequest,
        });
        const mergedDetails = mergeInProgressDataFromSummary(
          details,
          response.data
        );
        setDetails(mergedDetails);
        refreshRate();
      } catch {
        resetForm();
      } finally {
        setSubmitting(false);
      }
    },
  });

  const eligibleForDiscount =
    details.agencyInformation?.attributes.agencyBundle.attributes.eligible
      .value;

  if (!config || configLoading || !eligibleForDiscount) {
    return null;
  }

  const getProps = (fieldName: keyof AgencyBundleType, required: boolean) => {
    const fullFieldName = `${appId}.${fieldName}`;
    return {
      ...getFieldProps(fullFieldName),
      'data-testid': generateFieldDataTestId(
        `multi-line-discount-${fieldName}`
      ),
      variant: 'selectAutocomplete' as const,
      loading: isSubmitting,
      errorMessage:
        required && !getIn(values, fullFieldName) ? 'Required' : undefined,
      onChange: async (value: string) => {
        if (fieldName === 'policyType' && value === 'none') {
          setFieldValue(`${appId}.carrier`, '');
        }
        await setFieldValue(fullFieldName, value);
        submitForm();
      },
      onBlur: () => {
        dirty && submitForm();
      },
    };
  };

  return (
    <div data-testid="multi-line-discount">
      <SummarySection
        headerText="Multi-line Discount"
        subHeaderText="Customers may receive a discount if they currently have a personal property policy written by your agency."
        discountBadgeText="Discount Available"
      >
        <Row>
          <Column variant="third">
            <FormInput
              {...getProps('policyType', true)}
              options={config.agencyInformation.agencyBundle.policyType}
              label="What other lines do you insure for this customer?"
              disabled={isSubmitting}
            />
          </Column>
          <Column variant="third">
            <FormInput
              {...getProps('carrier', values[appId].policyType !== 'none')}
              options={config.agencyInformation.agencyBundle.carrier}
              label="Carrier or Underwriter"
              disabled={isSubmitting || values[appId].policyType === 'none'}
              placeholder="Select a carrier"
            />
          </Column>
          <Column />
        </Row>
      </SummarySection>
    </div>
  );
};
